/**
 * web-studio-sites D1, D3 — a studio's custom site, as its data block names it.
 *
 * `site:publish` writes `<script type="application/json" id="telify-site">` into
 * every page of the site, and the server writes the live version's into
 * Telify's own pages on that address. Its presence is what puts the website in
 * custom-site mode; its links and page addresses are what the page mover (D13)
 * resolves against, so a page never mixes its own version's links with
 * another's. Data only, never run.
 */
import { findPlugin, isOwn, resolveSiteOption } from '@telify/shared/browser';

export interface SiteLink {
  readonly path: string;
  readonly label: string;
}

/** The block's shape (`SiteData` in apps/api/src/modules/site/site-build.ts). */
export interface SiteData {
  readonly version: number;
  readonly links: { readonly home: SiteLink; readonly book: SiteLink };
  /** Every address the site's own pages answer. */
  readonly pages: readonly string[];
  readonly telifyPages: { readonly messageStudio: boolean };
  /**
   * A plug-in's words this site set in its `site.json`, by plug-in id, then option (design D14). Only what the site
   * set; absent when it set none. Each piece fills the rest from its plug-in's defaults.
   */
  readonly pluginOptions?: { readonly [pluginId: string]: { readonly [option: string]: SitePluginValue } };
}

/** A plug-in's words, as a site sets them: text, a list, or a group of named values. Always drawn as text. */
export type SitePluginValue = string | readonly SitePluginValue[] | { readonly [field: string]: SitePluginValue };

export const SITE_DATA_ID = 'telify-site';

let cached: { readonly el: Element | null; readonly site: SiteData | null } | undefined;

/** The page's site data, or null on the standard website. */
export function readSite(): SiteData | null {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById(SITE_DATA_ID);
  if (cached?.el === el) return cached.site;
  let site: SiteData | null = null;
  try {
    site = el?.textContent ? (JSON.parse(el.textContent) as SiteData) : null;
  } catch {
    site = null;
  }
  cached = { el, site };
  return site;
}

/**
 * A plug-in's words for this page (design D14): what this page's version of its site set, with anything unset filled
 * from the plug-in's declared default; the default on the standard website. Read from the data block, never from the
 * studio read, so a page shows its own version's words even when a publish lands while it loads.
 */
export function readPluginOption(pluginId: string, option: string): SitePluginValue {
  const options = findPlugin(pluginId)?.siteOptions;
  // Its own options only: a name every object inherits (toString, constructor) is never one it declares.
  const declared = options && isOwn(options, option) ? options[option] : undefined;
  if (!declared) throw new Error(`[site] the ${pluginId} plug-in declares no site option ${option}`);
  const set = readSite()?.pluginOptions?.[pluginId];
  return resolveSiteOption(declared, set && isOwn(set, option) ? set[option] : undefined);
}
